import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, TextField, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Box, Chip, InputAdornment, CircularProgress } from '@mui/material';
import { Search, User, Building2, Handshake, FileText } from 'lucide-react';
import { useGlobalSearch } from '@lingaai/shared-hooks';
import { useDebounce } from '../../hooks';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  Person: <User size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />,
  Organization: <Building2 size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />,
  Deal: <Handshake size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />,
  Fact: <FileText size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />,
};

const TYPE_ROUTES: Record<string, string> = {
  Person: '/people',
  Organization: '/organizations',
  Deal: '/deals',
  Opportunity: '/opportunities',
  Vehicle: '/vehicles',
  FundraisingOpportunity: '/fundraising',
};

export function GlobalSearch({ open, onClose }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const debouncedQuery = useDebounce(query, 300);

  const { data: results, isLoading } = useGlobalSearch(debouncedQuery);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const handleSelect = useCallback(
    (result: { id: string; type: string; parent_id: string | null; parent_type: string | null }) => {
      // For AMFI nodes, navigate to their parent
      const isAmfi = ['Attribute', 'Metric', 'Narrative'].includes(result.type);
      const targetId = isAmfi && result.parent_id ? result.parent_id : result.id;
      const targetType = isAmfi && result.parent_type ? result.parent_type : result.type;
      const basePath = TYPE_ROUTES[targetType] || '/nodes';
      navigate(`${basePath}/${targetId}`);
      onClose();
      setQuery('');
    },
    [navigate, onClose]
  );

  const handleClose = useCallback(() => {
    onClose();
    setQuery('');
    setSelectedIndex(0);
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!results || results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          }
          break;
      }
    },
    [results, selectedIndex, handleSelect]
  );

  const getDisplayName = (props: Record<string, unknown>): string => {
    return (props.name as string) || (props.content as string)?.slice(0, 50) || 'Untitled';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus
      PaperProps={{
        sx: { position: 'fixed', top: '15%', m: 0 },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search people, organizations, deals..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
              </InputAdornment>
            ),
            endAdornment: isLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { border: 'none' },
            },
            borderBottom: 1,
            borderColor: 'divider',
          }}
        />

        {debouncedQuery.length >= 2 && (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {results && results.length > 0 ? (
              <List disablePadding>
                {results.map((result, index) => (
                  <ListItem key={result.id} disablePadding>
                    <ListItemButton onClick={() => handleSelect(result)} selected={index === selectedIndex}>
                      <ListItemIcon sx={{ minWidth: 40 }}>{TYPE_ICONS[result.type] || <FileText size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}</ListItemIcon>
                      <ListItemText
                        primary={getDisplayName(result.props)}
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip label={result.type} size="small" variant="outlined" />
                            {result.match_source === 'amfi' && (
                              <Typography variant="caption" color="text.secondary">
                                via related data
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : !isLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No results found</Typography>
              </Box>
            ) : null}
          </Box>
        )}

        {debouncedQuery.length < 2 && debouncedQuery.length > 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Type at least 2 characters to search
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
